import {
    Box,
    Button,
    CrudMode,
    DataArray,
    DataDisplay,
    Grid,
    GridBreak,
    pn,
    Stack,
    Typography,
    useElementBreakpoints,
    ValidationErrors,
} from "@compass/core";
import { Input, Select } from "components";
import { Region, RegionEditResource, RegionViewResource, District } from "generated/openApi";
import produce from "immer";
import "moment-timezone";

export const RegionPageBody = (props: {
    crudMode: CrudMode;
    region: Region;
    setRegion: (value: Region) => void;
    viewResource: RegionViewResource;
    editResource: RegionEditResource;
    validationErrors: ValidationErrors;
}) => {
    const { breakpointsRef, smallFieldBreakpoints, largeFieldBreakpoints, fullFieldBreakpoints } =
        useElementBreakpoints();
    const properties = props.viewResource.properties || [];

    const nameField = () => {
        const propertyName = pn<Region>("name");
        const property = properties.find((x) => x.propertyName === propertyName);
        return props.crudMode === "view" ? (
            <DataDisplay label={property?.displayName}>{props.region.name}</DataDisplay>
        ) : (
            <Input
                modelProperty={property}
                errors={props.validationErrors[propertyName]}
                value={props.region.name}
                onChange={(e) =>
                    props.setRegion(
                        produce(props.region, (draft) => {
                            draft.name = e.target.value;
                        })
                    )
                }
            />
        );
    };

    const districtsField = () => {
        const propertyName = pn<Region>("districts");
        const property = properties.find((x) => x.propertyName === propertyName);
        const getDistrictLabel = (district: District) => (
            <Typography variant="inherit">{district.name}</Typography>
        );

        return props.crudMode === "view" ? (
            <DataDisplay label={property?.displayName}>
                <DataArray value={props.region.districts?.map((x) => getDistrictLabel(x))} />
            </DataDisplay>
        ) : (
            <Stack alignItems="flex-end">
                <Select
                    multiple
                    modelProperty={property}
                    errors={props.validationErrors[propertyName]}
                    value={props.region.districts}
                    options={props.editResource.districts}
                    getOptionLabel={(option) => option.name}
                    renderOption={(props, option) => (
                        <Box component="li" {...props}>
                            {getDistrictLabel(option)}
                        </Box>
                    )}
                    onChange={(e, values) =>
                        props.setRegion(
                            produce(props.region, (draft) => {
                                draft.districts = values;
                            })
                        )
                    }
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                />
                <Button
                    onClick={() =>
                        props.setRegion(
                            produce(props.region, (draft) => {
                                draft.districts = props.editResource.districts;
                            })
                        )
                    }
                    size="small"
                    textTransform="none"
                >
                    + Select all districts
                </Button>
            </Stack>
        );
    };

    return (
        <Grid ref={breakpointsRef} container spacing="sm">
            <Grid item xs={smallFieldBreakpoints}>
                {nameField()}
            </Grid>
            <GridBreak />
            <Grid item xs={fullFieldBreakpoints}>
                {districtsField()}
            </Grid>
        </Grid>
    );
};